// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import { disposeGroup } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

export const HIGHLIGHTABLE_OBJECT_PREFIX = 'HIGHLIGHTABLE_' as const;

export type HighlightableObjectName = `${typeof HIGHLIGHTABLE_OBJECT_PREFIX}${string}`;

/**
 * Returns the name of the highlightable object if this object is a highlightable object. Otherwise, returns null.
 * @param object The object to check.
 */
export const getHighlightableObjectName = (object: THREE.Object3D): HighlightableObjectName | null =>
  object.name.startsWith(HIGHLIGHTABLE_OBJECT_PREFIX) ? (object.name as HighlightableObjectName) : null;

export const toHighlightableObjectName = (suffix: string): HighlightableObjectName =>
  `${HIGHLIGHTABLE_OBJECT_PREFIX}${suffix}`;

/**
 * Adds event listeners to highlight and unhighlight a group and tracks whether the group is currently highlighted.
 */
export abstract class HighlightableGroup extends THREE.Group {
  protected isHighlighted = false;

  /**
   * Optional hook for geometries that need to rescale themselves to stay screen-size-consistent
   * as the camera zooms. Called each frame by {@link DimensionLabel.makeScaleConsistent}.
   */
  scaleForCamera?(camera: THREE.Camera): void;

  protected constructor(public readonly highlightableObjectName: HighlightableObjectName) {
    super();

    this.highlight = this.highlight.bind(this);
    this.unhighlight = this.unhighlight.bind(this);
  }

  /**
   * Adds hit detection to a given object.
   * This mutates the object by changing its name.
   * @param object The object to add hit detection to.
   */
  activateHitDetection(object: THREE.Object3D) {
    // oxlint-disable-next-line evolve/no-param-reassign
    object.name = this.highlightableObjectName;
  }

  highlight() {
    this.isHighlighted = true;
  }

  unhighlight() {
    this.isHighlighted = false;
  }

  dispose() {
    disposeGroup(this);
  }
}
